// components/orders/OrderModal.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseIcon } from '../../public/icons';
import { MemoryMagnet } from '../../public/images/home';
import MagnetsFrameModal from './MagnetsFrameModal';
import AudioGiftModal from './AudioGiftModal';
import orderService from '../../services/ordersRouteServce';

const OrderModal = ({ isOpen, onClose, order, onRefresh }) => {
  const navigate = useNavigate();

  /* 🟢 Controls for inline modals */
  const [magnetsModal, setMagnetsModal] = useState({
    open: false,
    orderId: null,
    giftId: null,
    gift: null
  });

  const [audioModal, setAudioModal] = useState({
    open: false,
    orderId: null,
    giftId: null,
    gift: null
  });

  /* 🟢 Map of giftId → { qrCodeUrl, publicUrl, pageSlug, hasContent } */
  const [contentByGiftId, setContentByGiftId] = useState({});

  /* ── Fetch each gift's content individually ── */
  const loadGiftContents = async () => {
    if (!order) return;

    const orderId = order.id || order._id;
    const rawGifts =
      order.items?.[0]?.customization?.futureGift?.futureGifts || [];

    if (rawGifts.length === 0) {
      setContentByGiftId({});
      return;
    }

    const results = await Promise.all(
      rawGifts.map(async (g) => {
        if (!g?.id) return [String(g?.id || ''), null];
        try {
          const res = await orderService.getFutureGiftContent(orderId, g.id);
          return [String(g.id), res?.data || null];
        } catch (err) {
          console.error(`Failed to load content for gift ${g.id}:`, err);
          return [String(g.id), null];
        }
      })
    );

    const map = {};
    results.forEach(([giftId, content]) => {
      if (!giftId || !content) return;
      map[giftId] = {
        qrCodeUrl: content.qrCodeUrl || '',
        publicUrl: content.publicUrl || '',
        pageSlug: content.pageSlug || '',
        hasContent: !!content.hasContent
      };
    });

    setContentByGiftId(map);
  };

  useEffect(() => {
    if (!isOpen || !order) return;
    let cancelled = false;

    (async () => {
      if (cancelled) return;
      await loadGiftContents();
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, order]);

  if (!isOpen || !order) return null;

  /* ── Normalize API fields ── */
  const orderNumber = order.orderNumber || order.id;
  const resolvedStatus = order.orderStatus || order.status || 'pending';
  const items = Array.isArray(order.items) ? order.items : [];

  /* ── Future gifts, merged with locally-fetched content ── */
  const rawFutureGifts =
    order.items?.[0]?.customization?.futureGift?.futureGifts || [];

  const futureGifts = rawFutureGifts.map((g) => {
    const extra = contentByGiftId[String(g.id)] || {};
    return {
      ...g,
      hasContent: extra.hasContent ?? !!g.hasContent,
      qrCodeUrl: extra.qrCodeUrl || g.qrCodeUrl || '',
      publicUrl: extra.publicUrl || g.publicUrl || '',
      pageSlug: extra.pageSlug || g.pageSlug || ''
    };
  });

  const hasFutureGifts = futureGifts.length > 0;
  const giftsWithQr = futureGifts.filter((g) => !!g.qrCodeUrl);

  /* ── Address ── */
  const shippingLines = (() => {
    if (order.delivery) {
      const d = order.delivery;
      return [
        d.address,
        [d.city, d.state].filter(Boolean).join(', '),
        d.country
      ].filter(Boolean);
    }
    if (order.shippingAddress) return [order.shippingAddress];
    return [];
  })();

  const handleClose = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleGiftAction = (gift, giftIndex) => {
    const orderId = order.id || order._id;

    if (!gift?.id) {
      console.warn(
        'Future gift has no id — run the backfill script before editing.'
      );
      return;
    }

    if (gift.giftType === 'magnets-frame') {
      setMagnetsModal({ open: true, orderId, giftId: gift.id, gift });
      return;
    }

    if (gift.giftType === 'audio-gift') {
      setAudioModal({ open: true, orderId, giftId: gift.id, gift });
      return;
    }

    const baseState = {
      gift,
      giftId: gift.id,
      giftIndex,
      orderId,
      orderNumber,
      isEditing: false
    };

    switch (gift.giftType) {
      case 'digital-memory':
        navigate('/account/future-gifts/create-memory', { state: baseState });
        break;
      default:
        navigate('/future-gifts/create', { state: baseState });
        break;
    }

    onClose();
  };

  const getTimelineData = () => {
    const t = order.timeline || {};
    const statusOrder = [
      'pending',
      'processing',
      'in_production',
      'content_review',
      'shipped',
      'delivered'
    ];
    const currentIdx = statusOrder.indexOf(resolvedStatus);

    const reached = (step) => {
      const stepIdx = statusOrder.indexOf(step);
      if (currentIdx === -1) return !!t[step];
      return stepIdx !== -1 && currentIdx >= stepIdx;
    };

    return [
      {
        id: 'confirmed',
        label: 'Order Confirmed',
        description: "We've received your order",
        date: t.confirmed || '',
        completed: true
      },
      {
        id: 'production',
        label: 'In Production',
        description: 'Your items are being carefully put together.',
        date: t.production || '',
        completed: reached('in_production') || reached('processing')
      },
      {
        id: 'shipped',
        label: 'Shipped',
        description: 'Your gift is on its way.',
        date: t.shipped || '',
        completed: reached('shipped')
      },
      {
        id: 'outForDelivery',
        label: 'Out for Delivery',
        description: 'Your gift is arriving today.',
        date: t.outForDelivery || '',
        completed: !!t.outForDelivery
      },
      {
        id: 'delivered',
        label: 'Delivered',
        description: 'Your Kapta package has been delivered successfully.',
        date: t.delivered || '',
        completed: reached('delivered')
      }
    ];
  };

  const timelineData = getTimelineData();

  const formatItemLabel = (item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') {
      const qty = item.quantity > 1 ? ` x${item.quantity}` : '';
      return `${item.title || 'Item'}${qty}`;
    }
    return '';
  };

  const formatRevealDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const giftTypeLabel = (type) => {
    switch (type) {
      case 'digital-memory':
        return 'Digital Memory';
      case 'magnets-frame':
        return 'Memory Magnet';
      case 'audio-gift':
        return 'Audio Gift';
      default:
        return type || 'Future Gift';
    }
  };

  const closeMagnetsModal = () => {
    setMagnetsModal({ open: false, orderId: null, giftId: null, gift: null });
  };

  const closeAudioModal = () => {
    setAudioModal({ open: false, orderId: null, giftId: null, gift: null });
  };

  const handleSaved = async () => {
    try {
      if (onRefresh) await onRefresh();
      await loadGiftContents();
    } catch (err) {
      console.error('Refresh after save failed:', err);
    }
    onClose?.();
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={handleOverlayClick}
      />

      <div className="fixed right-0 top-0 h-full w-full md:w-[600px] text-left bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out shadow-2xl">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-2 mb-3 border-b border-[#DFDFDF]">
            <div className="text-left">
              <h2 className="text-2xl font-medium text-[#2D3748]">
                Order #{orderNumber}
              </h2>
              <p className="text-[#6B635D] text-sm">
                {order.date} {order.time}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  className="text-xs text-[#6B635D] hover:text-[#1C2A46] border border-[#DFDFDF] px-2 py-1"
                  type="button"
                  title="Refresh"
                >
                  Refresh
                </button>
              )}
              <button
                onClick={handleClose}
                className="text-[#1E1E1E] hover:text-opacity-70 border-2 p-1 border-[#DFDFDF]"
                type="button"
              >
                <CloseIcon className="w-5 h-5 text-[#4A5568]" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6">
            {/* Product Section */}
            <div className="flex items-start gap-4 mb-8">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-gray-200 rounded flex-shrink-0">
                  <img
                    src={items[0]?.image || MemoryMagnet}
                    alt="Order item"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex justify-between items-start mb-1">
                  <h3 className="font-medium uppercase text-[#2D3748] text-base">
                    {order.brand || items[0]?.slug}
                  </h3>
                  <span className="text-base font-medium text-secondary">
                    ₦{Number(order.total || 0).toLocaleString()}
                  </span>
                </div>
                <p className="text-[#718096] text-sm italic mb-3">
                  {order.tagline || items[0]?.description || ''}
                </p>

                <div className="space-y-1">
                  {items.map((item, index) => (
                    <div
                      key={item._id || item.productId || index}
                      className="flex items-start"
                    >
                      <span className="mr-2 text-[#2D3748] text-sm">•</span>
                      <span className="text-[#2D3748] text-sm">
                        {formatItemLabel(item)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="mb-8 border border-[#E4E4E4] px-4 py-2.5">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium text-secondary text-base">
                  Shipping Address
                </h4>
                {/* <button className="text-base text-secondary font-normal underline">
                  Edit Address
                </button> */}
              </div>
              <div className="text-[#2D3748] text-sm leading-relaxed">
                {shippingLines.length > 0 ? (
                  shippingLines.map((line, i) => <p key={i}>{line}</p>)
                ) : (
                  <p className="text-[#718096]">No address on file</p>
                )}
              </div>
            </div>

            {/* Order Timeline */}
            <div className="mb-8">
              <div className="space-y-6">
                {timelineData.map((step, index) => (
                  <div key={step.id} className="relative">
                    {index < timelineData.length - 1 && (
                      <div className="absolute left-[15px] top-8 bottom-0 w-[2px] bg-[#E2E8F0] -mb-6"></div>
                    )}

                    <div className="flex items-start gap-4">
                      <div
                        className={`relative z-10 w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                          step.completed
                            ? 'border-[#55000C]'
                            : 'border-[#DBDBDB]'
                        }`}
                      >
                        {step.completed && (
                          <svg
                            className="w-4 h-4 text-[#55000C]"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <h4
                            className={`font-normal text-base ${
                              step.completed
                                ? 'text-[#1C2A46]'
                                : 'text-[#6B635D]'
                            }`}
                          >
                            {step.label}
                          </h4>
                          {step.date && (
                            <span className="text-sm font-light text-[#1C2A46] ml-4 flex-shrink-0">
                              {step.date}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-light text-[#6B635D] mt-1">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Future Gifts Section */}
            {hasFutureGifts && (
              <div className="mb-6 bg-[#FAF7F0] border border-[#E4E4E4] p-5">
                <h4 className="font-medium text-secondary text-lg mb-5">
                  Future Gift linked to this order
                </h4>

                <div className="space-y-5 mb-5">
                  {futureGifts.map((gift, i) => {
                    const hasContent = !!gift.hasContent;
                    const label = hasContent ? 'Content Added' : 'Add Content';

                    return (
                      <div
                        key={gift.id || i}
                        className="flex items-start justify-between gap-4"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-[#2D3748] text-base font-normal mb-0.5 truncate">
                            {giftTypeLabel(gift.giftType)}
                          </p>

                          {gift.deliveryDate && (
                            <p className="text-[#718096] text-sm font-light">
                              Reveals on{' '}
                              {formatRevealDate(gift.deliveryDate)}
                            </p>
                          )}
                        </div>

                        {hasContent ? (
                          <span
                            className="text-sm font-normal flex-shrink-0 mt-1 text-[#718096] cursor-default"
                            title="Content has already been added for this gift"
                          >
                            {label}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleGiftAction(gift, i)}
                            className="text-sm font-normal flex-shrink-0 mt-1 text-[#55000C] hover:underline transition-colors"
                            title="Add content for this gift"
                          >
                            {label}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* 🟢 QR Code(s) — rendered inline, one block per gift */}
                {giftsWithQr.length > 0 && (
                  <div className="bg-[#FAF7F0] border-2 border-dashed border-[#D9CFC0] p-4">
                    <h5 className="text-[#2D3748] text-base font-medium mb-1">
                      QR Code
                    </h5>
                    <p className="text-[#718096] text-sm font-light mb-4">
                      Scan to open the memory page. This QR connects the
                      physical gift to its digital content.
                    </p>

                    <div className="space-y-6">
                      {giftsWithQr.map((gift, idx) => (
                        <div
                          key={gift.id || idx}
                          className="flex flex-col sm:flex-row w-fit  items-center gap-4 border border-[#E4E4E4] bg-[#FCF7EE] p-4"
                        >
                          <img
                            src={gift.qrCodeUrl}
                            alt={`QR for ${giftTypeLabel(gift.giftType)}`}
                            className="w-32 h-32 object-contain flex-shrink-0 border border-[#E4E4E4] bg-white"
                          />
                         
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <MagnetsFrameModal
        isOpen={magnetsModal.open}
        onClose={closeMagnetsModal}
        orderId={magnetsModal.orderId}
        giftId={magnetsModal.giftId}
        gift={magnetsModal.gift}
        orderNumber={orderNumber}
        onSaved={handleSaved}
      />

      <AudioGiftModal
        isOpen={audioModal.open}
        onClose={closeAudioModal}
        orderId={audioModal.orderId}
        giftId={audioModal.giftId}
        gift={audioModal.gift}
        orderNumber={orderNumber}
        onSaved={handleSaved}
      />
    </>
  );
};

export default OrderModal;